import React, { useRef } from 'react'

import * as Sentry from '@sentry/react'

import { RoundButton } from '@/components/buttons/RoundButton'
import { ExitIcon } from '@/components/Icons/ExitIcon'
import { Loader } from '@/components/Loader'
import { Modal } from '@/components/Modal'
import { findProductByHashOrId } from '@/utils/products'
import { COLOR_BLUE } from '@/utils/styles'
import { useBreakpoints } from '@/utils/useBreakpoints'
import useOnClickOutside from '@/utils/useOnClickOutside'

import { useProducts } from '../common/useProducts'
import { Product } from '../products/Product'
import useProductOverlay from './useProductOverlay'

import styles from './ProductOverlay.module.scss'

export const ProductOverlay: React.FC<{ productId: string }> = ({ productId }) => {
  const { products, isFetching: isFetchingProducts } = useProducts()
  const product = products.find(findProductByHashOrId(productId))
  const productNotFound = !isFetchingProducts && !product

  const { closeOverlay } = useProductOverlay()

  const contentRef = useRef<HTMLDivElement>(null)
  const { from } = useBreakpoints()

  useOnClickOutside(contentRef, () => {
    if (from('laptop-small')) {
      closeOverlay()
    }
  })

  if (productNotFound) {
    Sentry?.captureEvent({ message: `product ${productId} not found in product overlay` })
  }

  return (
    <Modal contentClassName={styles.ModalContent} position="Top" data-cy="product-modal" onClose={closeOverlay}>
      <div className={styles.Content}>
        {productNotFound || !product ? (
          <Loader strokeColor={COLOR_BLUE} />
        ) : (
          <>
            <RoundButton
              additionalClasses={styles.CloseButton}
              onClick={closeOverlay}
              data-cy="product-modal-close-button"
            >
              <ExitIcon />
            </RoundButton>
            <div ref={contentRef}>
              <Product product={product} initialProductCopy={null} isProductPage={false} />
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
